import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api, ApiError, type ApiClientMe } from "./api";

// Mirrors the owner-side AuthProvider almost line-for-line, but hits the
// /api/c/* surface instead. We keep both providers mounted on the app so
// the same browser can be both an owner and an invited client without
// either side blowing the other away.

type ClientAuthState =
  | { status: "loading" }
  | { status: "anon" }
  | { status: "authed"; me: ApiClientMe };

type ClientAuthContextValue = {
  state: ClientAuthState;
  // Request a magic link by email. Always resolves (no leak on miss).
  // shareToken is optional — passed through when the request originates
  // from a /p/:token reviewer surface so a brand-new customer (no invite,
  // no comment history) can still self-sign-up from a share link.
  requestLogin: (email: string, shareToken?: string) => Promise<void>;
  // Consume a magic-link token (called by the /c/login?token=… landing).
  // shareToken comes from the `&share=` query param on the magic URL so
  // the server can materialize a project_invitees row at consume time.
  consumeToken: (token: string, shareToken?: string) => Promise<void>;
  logout: () => Promise<void>;
  reload: () => Promise<void>;
};

const ClientAuthContext = createContext<ClientAuthContextValue | null>(null);

export function ClientAuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ClientAuthState>({ status: "loading" });

  const reload = useCallback(async () => {
    try {
      const res = await api.clientMe();
      setState(res.me ? { status: "authed", me: res.me } : { status: "anon" });
    } catch (err) {
      if (!(err instanceof ApiError) || err.status !== 401) {
        console.warn("clientAuth: /me failed", err);
      }
      setState({ status: "anon" });
    }
  }, []);

  useEffect(() => {
    // Symmetric with AuthProvider: don't probe /api/c/me on the reviewer
    // surface. The share page never reads this context, and any pre-existing
    // client session shouldn't render client chrome over a public link.
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/p/")) {
      setState({ status: "anon" });
      return;
    }
    void reload();
  }, [reload]);

  const requestLogin = useCallback(async (email: string, shareToken?: string) => {
    await api.clientLogin(email, shareToken);
  }, []);

  const consumeToken = useCallback(async (token: string, shareToken?: string) => {
    const res = await api.clientConsumeToken(token, shareToken);
    setState({ status: "authed", me: res.me });
  }, []);

  const logout = useCallback(async () => {
    await api.clientLogout();
    setState({ status: "anon" });
  }, []);

  const value = useMemo<ClientAuthContextValue>(
    () => ({ state, requestLogin, consumeToken, logout, reload }),
    [state, requestLogin, consumeToken, logout, reload],
  );

  return (
    <ClientAuthContext.Provider value={value}>{children}</ClientAuthContext.Provider>
  );
}

export function useClientAuth(): ClientAuthContextValue {
  const ctx = useContext(ClientAuthContext);
  if (!ctx) throw new Error("useClientAuth must be used inside <ClientAuthProvider>");
  return ctx;
}
